import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../lib/api-client';
import { formatMoney } from '../lib/money';
import { apiError } from '../lib/utils';
import { takeDuplicate } from '../lib/duplicate';
import { peekPendingOfferFiles, takePendingOfferFiles } from '../lib/pending-offer-files';
import { loadedRecordKey, useEditGuard } from '../lib/dirty';
import { onOfferSaved } from '../lib/record-assist';
import { OfferWorkspace } from '../components/documents/OfferWorkspace';
import { IssueHistory, MarkStatus, revisionLabel } from '../components/documents/IssueHistory';
import { ProposalPreview } from '../components/documents/ProposalPreview';
import { Alert } from '../components/ui/Badge';
import { useNotice } from '../components/ui/Notice';
import { Button } from '../components/ui/Button';
import { EditWithAiButton, RecordAssistDialog } from '../components/records/RecordAssistDialog';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Field, GrowingText, Input, Select, TextArea } from '../components/ui/Input';
import { customerChoices, useFreshRows } from '../lib/select-choices';
import { useOurContact } from '../lib/our-contact';
import { EmailTemplateSelect, SupplierTemplateSelect } from '../components/documents/TemplateSelects';
import { RecordLink, SectionCard } from '../components/ui/chrome';
import { Award, CircleAlert, FileText, Layers, ListChecks, MessageSquare, MoreHorizontal, Package, Receipt, Route, type LucideIcon } from 'lucide-react';
import { deleteProposalFile, filesOf, ProposalFileLinks, uploadProposalFile, type ProposalFileLink } from '../components/documents/ProposalFiles';
import { AddOfferPartDialog } from '../components/documents/AddOfferPartDialog';
import { OfferDuplicateDialog } from '../components/documents/OfferDuplicateDialog';
import { ReviewChoiceDialog } from '../components/documents/ReviewChoiceDialog';

export function ProposalPage({ recordId, initial, bare, onCreated, paired, saveAll, registerSave, onDirty, sectionTab, onSelectTab }: {
  recordId?: string;
  initial?: Record<string, unknown> | null;
  bare?: boolean;
  onCreated?: (id: string) => void;
  paired?: boolean;
  saveAll?: () => Promise<boolean>;
  registerSave?: (save: () => Promise<string | void>) => void;
  onDirty?: (dirty: boolean) => void;
  sectionTab?: string;
  onSelectTab?: (id: string) => void;
} = {}) {
  const params = useParams();
  const id = recordId ?? params.id;
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const notice = useNotice();
  const [customers, reloadCustomers] = useFreshRows<{ id: string; code?: string; name: string; description?: string }>('/api/customers');
  const [row, setRow] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const numberTouched = useRef(false);
  const [docKind, setDocKind] = useState('services');
  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [summary, setSummary] = useState('');
  const [scope, setScope] = useState('');
  const [issuedOn, setIssuedOn] = useState(new Date().toISOString().slice(0, 10));
  const [preparedFor, setPreparedFor] = useState('');
  const [preparedBy, setPreparedBy] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  useOurContact(!id || id === 'new', (contact) => {
    setPreparedBy((value) => value || contact.contactName);
    setContactRole((value) => value || contact.contactRole);
    setContactEmail((value) => value || contact.contactEmail);
    setContactPhone((value) => value || contact.contactPhone);
  });
  const [understanding, setUnderstanding] = useState('');
  const [approach, setApproach] = useState('');
  const [stages, setStages] = useState('');
  const [deliverables, setDeliverables] = useState('');
  const [commercial, setCommercial] = useState('');
  const [assumptions, setAssumptions] = useState('');
  const [whyMaxiron, setWhyMaxiron] = useState('');
  const [nextSteps, setNextSteps] = useState('');
  const [recipients, setRecipients] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [saveState, setSaveState] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [section, setSection] = useState('summary');
  const [linkedQuote, setLinkedQuote] = useState<{ id: string; number: string; amountPence: number; vatPence: number | null; currency: string; status: string; sharePointUrl?: string } | null>(null);
  const [files, setFiles] = useState<ProposalFileLink[]>([]);
  const [fileBusy, setFileBusy] = useState(false);
  const markCleanRef = useRef<() => void>(() => {});
  const duplicateApplied = useRef(false);
  const [unsavedCopy, setUnsavedCopy] = useState(false);
  const [askAdd, setAskAdd] = useState(false);
  const [askDuplicate, setAskDuplicate] = useState(false);
  const [askReview, setAskReview] = useState(false);
  const [assist, setAssist] = useState<'create' | 'edit' | null>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const skipPreviewSync = useRef(false);

  function applyProposal(found: Record<string, unknown>) {
    setCustomerId(String(found.customerId || ''));
    setDocKind(String(found.docKind || 'services'));
    setTitle(String(found.title || ''));
    setPurpose(String(found.purpose || ''));
    setSummary(String(found.summary || ''));
    setScope(String(found.scope || ''));
    setIssuedOn(String(found.issuedOn || new Date().toISOString().slice(0, 10)));
    setPreparedFor(String(found.preparedFor || ''));
    setPreparedBy(String(found.preparedBy || ''));
    setContactRole(String(found.contactRole || ''));
    setContactEmail(String(found.contactEmail || ''));
    setContactPhone(String(found.contactPhone || ''));
    setUnderstanding(String(found.understanding || ''));
    setApproach(String(found.approach || ''));
    setStages(String(found.stages || ''));
    setDeliverables(String(found.deliverables || ''));
    setCommercial(String(found.commercial || ''));
    setAssumptions(String(found.assumptions || ''));
    setWhyMaxiron(String(found.whyMaxiron || ''));
    setNextSteps(String(found.nextSteps || ''));
    setRecipients(Array.isArray(found.recipients) ? (found.recipients as string[]).join(', ') : '');
    setEmailSubject(String(found.emailSubject || ''));
    setEmailBody(String(found.emailBody || ''));
    setDocumentNumber(String(found.number || ''));
    numberTouched.current = Boolean(found.number);
    setFiles(filesOf(found));
  }

  useEffect(() => {
    if (initial) {
      if (duplicateApplied.current) return;
      duplicateApplied.current = true;
      setRow(null);
      setUnsavedCopy(true);
      applyProposal({ ...initial, title: '' });
      return;
    }
    if (!id || id === 'new') {
      if (duplicateApplied.current) return;
      setRow(null);
      if (search.get('duplicate') === '1') {
        const copy = takeDuplicate('proposal');
        if (copy) {
          duplicateApplied.current = true;
          setUnsavedCopy(true);
          applyProposal(copy);
          return;
        }
      }
      setCustomerId(search.get('customerId') || '');
      setTitle(search.get('title') || '');
      setDocumentNumber('');
      numberTouched.current = false;
      return;
    }
    setError('');
    void apiGet<Record<string, unknown>>(`/api/proposals/${id}`).then((found) => {
      setRow(found);
      applyProposal(found);
    }).catch((err) => setError(apiError(err, 'Could not open this proposal')));
  }, [id]);

  useEffect(() => {
    if (row?.id || numberTouched.current || !customerId) return;
    const handle = window.setTimeout(() => {
      void apiGet<{ number: string }>(`/api/proposals/next-number?customerId=${encodeURIComponent(customerId)}&docKind=${encodeURIComponent(docKind)}`)
        .then((next) => { if (!numberTouched.current && next.number) setDocumentNumber(next.number); })
        .catch(() => undefined);
    }, 200);
    return () => window.clearTimeout(handle);
  }, [customerId, docKind, row?.id]);

  const locked = row?.status === 'issued' || row?.status === 'accepted';

  function body() {
    return {
      customerId, title, purpose, summary, scope, issuedOn, docKind,
      ...(row?.id || !documentNumber ? {} : { number: documentNumber }),
      preparedFor, preparedBy, contactRole, contactEmail, contactPhone, understanding, approach, stages, deliverables, commercial, assumptions, whyMaxiron, nextSteps,
      emailSubject, emailBody,
      recipients: recipients.split(/[,;\n]+/).map((item) => item.trim()).filter(Boolean),
      ...(row?.id ? {} : { externalFiles: files }),
    };
  }

  async function save(options?: { stay?: boolean }) {
    if (locked) return row?.id ? String(row.id) : undefined;
    setError('');
    setSaveState('saving');
    try {
      const creating = !row?.id;
      const linkQuotation = creating ? (search.get('quotationId') || '') : '';
      let saved = creating ? await apiPost<Record<string, unknown>>('/api/proposals', body()) : await apiPut<Record<string, unknown>>(`/api/proposals/${row.id}`, body());
      if (linkQuotation && saved.id) await apiPut(`/api/quotations/${linkQuotation}`, { proposalId: String(saved.id) });
      if (creating && search.get('attach') === '1' && saved.id) {
        for (const file of takePendingOfferFiles('proposal')) saved = await uploadProposalFile(String(saved.id), file);
      }
      setRow(saved);
      setFiles(filesOf(saved));
      markCleanRef.current();
      setSaveState('saved');
      notice.success('Saved');
      const savedId = saved.id ? String(saved.id) : undefined;
      if (!options?.stay && !recordId && savedId) {
        if (onCreated) onCreated(savedId);
        else if (!paired) navigate(`/proposals/${savedId}`);
      }
      return savedId;
    } catch (err) {
      setSaveState('failed');
      notice.failure(apiError(err, 'Save failed'));
    }
  }

  registerSave?.(() => save());
  const guardKey = loadedRecordKey(id, row?.id ? String(row.id) : undefined) || ((initial || paired) && !row?.id ? 'new' : '');
  markCleanRef.current = useEditGuard(guardKey, JSON.stringify(body()), save, paired ? { silent: true, onDirty } : undefined);

  useEffect(() => {
    if (!row?.id) return;
    void apiGet<{ id: string; number?: string; kind?: string; proposalId?: string; amountPence?: number; vatPence?: number | null; currency?: string }[]>('/api/commercial').then(async (rows) => {
      const quote = (rows || []).find((item) => item.kind === 'quotation' && item.proposalId === row.id);
      if (!quote) {
        setLinkedQuote(null);
        return;
      }
      const full = await apiGet<{ sharePointUrl?: string; status?: string }>(`/api/quotations/${quote.id}`).catch(() => ({ sharePointUrl: '', status: '' }));
      setLinkedQuote({ id: quote.id, number: String(quote.number || ''), amountPence: Number(quote.amountPence || 0), vatPence: quote.vatPence == null ? null : Number(quote.vatPence), currency: String(quote.currency || 'GBP'), status: String(full.status || ''), sharePointUrl: full.sharePointUrl || '' });
    }).catch(() => setLinkedQuote(null));
  }, [row?.id]);

  useEffect(() => onOfferSaved((ids) => {
    if (!ids.proposalId || ids.proposalId !== String(row?.id || '')) return;
    void apiGet<Record<string, unknown>>(`/api/proposals/${ids.proposalId}`).then((found) => { setRow(found); applyProposal(found); });
  }), [row?.id]);

  async function addFile(file: File) {
    setFileBusy(true);
    try {
      const existed = Boolean(row?.id);
      const savedId = existed ? String(row?.id) : await save({ stay: true });
      if (!savedId) return;
      const saved = await uploadProposalFile(savedId, file);
      setRow(saved);
      setFiles(filesOf(saved));
      notice.success('File attached');
      if (!existed) {
        if (onCreated) onCreated(savedId);
        else if (!paired) navigate(`/proposals/${savedId}`);
      }
    } catch (err) {
      notice.failure(apiError(err, 'Could not attach that file'));
    } finally {
      setFileBusy(false);
    }
  }

  async function removeFile(path: string) {
    if (!row?.id) {
      setFiles((current) => current.filter((file) => file.path !== path));
      return;
    }
    setFileBusy(true);
    try {
      const saved = await deleteProposalFile(String(row.id), path);
      setRow(saved);
      setFiles(filesOf(saved));
    } catch (err) {
      notice.failure(apiError(err, 'Could not remove that file'));
    } finally {
      setFileBusy(false);
    }
  }

  async function revise() {
    try {
      const saved = await apiPost<{ id: string }>(`/api/proposals/${row?.id}/revise`, {});
      navigate(`/proposals/${saved.id}`);
      notice.success('Revision created');
    } catch (err) {
      notice.failure(apiError(err, 'Revise failed'));
    }
  }

  const tab = sectionTab || search.get('tab') || 'proposal';
  const customerName = customers.find((item) => item.id === customerId)?.name || '';
  const sections: { id: string; title: string; hint: string; icon: LucideIcon; value: string; set: (value: string) => void }[] = [
    { id: 'summary', title: 'Executive summary', hint: 'Explain the outcome and why it matters.', icon: FileText, value: summary, set: setSummary },
    { id: 'understanding', title: 'Our understanding', hint: 'Describe the customer situation and needs.', icon: MessageSquare, value: understanding, set: setUnderstanding },
    { id: 'approach', title: 'Proposed approach', hint: 'How the work will be done.', icon: Route, value: approach, set: setApproach },
    { id: 'stages', title: 'Engagement stages', hint: 'The stages and who does them.', icon: Layers, value: stages, set: setStages },
    { id: 'deliverables', title: 'Deliverables', hint: 'What the customer receives.', icon: Package, value: deliverables, set: setDeliverables },
    { id: 'commercial', title: 'Commercial summary', hint: 'Commercial notes. Amounts stay on the quotation.', icon: Receipt, value: commercial, set: setCommercial },
    { id: 'assumptions', title: 'Assumptions', hint: 'What this proposal assumes.', icon: CircleAlert, value: assumptions, set: setAssumptions },
    { id: 'why', title: 'Why MaxIron', hint: 'Why this team.', icon: Award, value: whyMaxiron, set: setWhyMaxiron },
    { id: 'next', title: 'Next steps', hint: 'What happens after acceptance.', icon: ListChecks, value: nextSteps, set: setNextSteps },
  ];
  function scrollPane(box: HTMLElement | null, elementId: string) {
    const node = document.getElementById(elementId);
    if (!box || !node) return;
    skipPreviewSync.current = true;
    const top = node.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    box.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    window.setTimeout(() => { skipPreviewSync.current = false; }, 800);
  }

  function followPreview() {
    const form = centerRef.current;
    const preview = previewRef.current;
    if (!form || skipPreviewSync.current) return;
    const edge = form.getBoundingClientRect().top + 12;
    let current = sections[0]?.id || '';
    for (const item of sections) {
      const node = document.getElementById(`section-${item.id}`);
      if (node && node.getBoundingClientRect().top <= edge) current = item.id;
    }
    if (current) setSection((prev) => (prev === current ? prev : current));
    if (!preview || !previewing) return;
    const formMax = form.scrollHeight - form.clientHeight;
    const previewMax = preview.scrollHeight - preview.clientHeight;
    if (formMax <= 0 || previewMax <= 0) return;
    preview.scrollTop = (form.scrollTop / formMax) * previewMax;
  }

  function focusSection(nextId: string) {
    setSection(nextId);
    scrollPane(centerRef.current, `section-${nextId}`);
    scrollPane(previewRef.current, `preview-${nextId}`);
  }

  const formView = (
    <>
      {error ? <Alert error>{error}</Alert> : null}
      {unsavedCopy ? <p className="mb-3 text-sm text-[var(--bo-muted)]">Not saved yet. Save creates a new proposal.</p> : null}
      {tab === 'activity' ? (
        <IssueHistory issues={row?.issues} sharePointUrl={typeof row?.sharePointUrl === 'string' ? row.sharePointUrl : ''} />
      ) : tab === 'overview' ? (
        <SectionCard title="This offer" hint="The proposal and quotation stay linked. Amounts live on the quotation.">
          <Field label="Reference"><Input value={documentNumber || (row?.number ? String(row.number) : '')} disabled={Boolean(row?.id)} onChange={(event) => { numberTouched.current = true; setDocumentNumber(event.target.value); }} /></Field>
          {linkedQuote ? <RecordLink className="mt-2 inline-block text-sm text-[var(--bo-link)]" to={`/quotations/${linkedQuote.id}`} backLabel={`Back to ${documentNumber || 'proposal'}`}>Open quotation {linkedQuote.number}</RecordLink> : <p className="mt-2 text-sm text-[var(--bo-muted)]">No quotation linked yet.</p>}
          {linkedQuote ? <p className="mt-3 text-sm">Total <span className="font-semibold tabular-nums">{formatMoney(linkedQuote.vatPence == null ? linkedQuote.amountPence : linkedQuote.amountPence + linkedQuote.vatPence, linkedQuote.currency)}</span></p> : null}
        </SectionCard>
      ) : (
        <div className={`grid h-[calc(100vh-16rem)] items-stretch gap-4 ${previewing ? 'xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : ''}`}>
        {linkedQuote ? <div className="col-span-full rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] px-4 py-3"><div className="text-xs text-[var(--bo-muted)]">Quotation {linkedQuote.number} total</div><div className="text-lg font-semibold tabular-nums">{formatMoney(linkedQuote.vatPence == null ? linkedQuote.amountPence : linkedQuote.amountPence + linkedQuote.vatPence, linkedQuote.currency)}</div><div className="text-xs text-[var(--bo-faint)]">Read only. The lines stay on the quotation.</div></div> : null}
        <div className="grid min-h-0 items-stretch gap-4 xl:grid-cols-[200px_minmax(0,1fr)_280px]">
          <nav className="space-y-1 overflow-y-auto text-sm">
            <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--bo-faint)]">Proposal</div>
            {sections.map((item) => (
              <button key={item.id} type="button" className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left ${section === item.id ? 'bg-[#895CF6] text-white' : 'text-[var(--bo-muted)]'}`} onClick={() => focusSection(item.id)}><item.icon className="h-4 w-4 shrink-0" />{item.title}</button>
            ))}
          </nav>
          <div ref={centerRef} className="min-h-0 space-y-4 overflow-y-auto pr-1" onScroll={followPreview}>
            {sections.map((item) => (
              <div id={`section-${item.id}`} key={item.id}>
                <SectionCard title={item.title} hint={item.hint}>
                  <GrowingText value={item.value} disabled={locked} onChange={item.set} />
                </SectionCard>
              </div>
            ))}
          </div>
          <div className="min-h-0 space-y-4 overflow-y-auto">
            <SectionCard title="Document details">
              <div className="space-y-3">
                <Field label="Customer"><Select searchable choices={customerChoices(customers)} onOpen={reloadCustomers} value={customerId} disabled={locked} onChange={(event) => setCustomerId(event.target.value)} /></Field>
                <Field label="Kind"><Select value={docKind} disabled={locked} onChange={(event) => setDocKind(event.target.value)}><option value="services">Services</option><option value="licence">Licences</option><option value="support">Support and cloud</option><option value="commercial">Commercial</option></Select></Field>
                <Field label="Title"><Input value={title} disabled={locked} onChange={(event) => setTitle(event.target.value)} /></Field>
                <Field label="Prepared for"><Input value={preparedFor} disabled={locked} onChange={(event) => setPreparedFor(event.target.value)} /></Field>
                <SupplierTemplateSelect onApply={(contact) => { setPreparedBy(contact.contactName); setContactRole(contact.contactRole); setContactEmail(contact.contactEmail); setContactPhone(contact.contactPhone); }} />
                <Field label="Prepared by"><Input value={preparedBy} disabled={locked} onChange={(event) => setPreparedBy(event.target.value)} /></Field>
                <Field label="Role"><Input value={contactRole} disabled={locked} onChange={(event) => setContactRole(event.target.value)} /></Field>
                <Field label="Email"><Input value={contactEmail} disabled={locked} onChange={(event) => setContactEmail(event.target.value)} /></Field>
                <Field label="Telephone"><Input value={contactPhone} disabled={locked} onChange={(event) => setContactPhone(event.target.value)} /></Field>
                <Field label="Date"><Input type="date" value={issuedOn} disabled={locked} onChange={(event) => setIssuedOn(event.target.value)} /></Field>
                <Field label="Document reference"><Input value={documentNumber || (row?.number ? String(row.number) : '')} disabled={Boolean(row?.id) || locked} onChange={(event) => { numberTouched.current = true; setDocumentNumber(event.target.value); }} /></Field>
                <EmailTemplateSelect kind="proposal" values={{ number: documentNumber || (row?.number ? String(row.number) : ''), customer: customerName, title }} onApply={(subject, message) => { setEmailSubject(subject); setEmailBody(message); }} />
                <Field label="Email subject"><Input value={emailSubject} disabled={locked} onChange={(event) => setEmailSubject(event.target.value)} /></Field>
                <Field label="Email message"><TextArea value={emailBody} disabled={locked} onChange={(event) => setEmailBody(event.target.value)} /></Field>
              </div>
            </SectionCard>
            <SectionCard title="External proposal" hint="The sections on the left stay the reference. Attach the full proposal in any format, including PowerPoint. Issue sends that file with the reference proposal and the quotation, and keeps a SharePoint link for each.">
              {search.get('attach') === '1' && peekPendingOfferFiles('proposal').length ? <p className="text-sm text-[var(--bo-muted)]">Attached when you save: {peekPendingOfferFiles('proposal').map((file) => file.name).join(', ')}</p> : null}
              <ProposalFileLinks
                files={files}
                proposalUrl={row?.sharePointUrl ? String(row.sharePointUrl) : ''}
                proposalLabel={row?.number ? `${row.number}.pdf` : 'Proposal'}
                quotationUrl={linkedQuote?.sharePointUrl}
                quotationLabel={linkedQuote?.number ? `${linkedQuote.number}.pdf` : 'Quotation'}
                locked={row?.status === 'accepted'}
                busy={fileBusy}
                onPick={(file) => void addFile(file)}
                onRemove={(path) => void removeFile(path)}
              />
            </SectionCard>
            <SectionCard title="Linked quotation" hint="Commercial amounts are managed in the quotation.">
              {linkedQuote ? <RecordLink className="text-sm text-[var(--bo-link)]" to={`/quotations/${linkedQuote.id}`} backLabel={`Back to ${documentNumber || 'proposal'}`}>Open quotation {linkedQuote.number}</RecordLink> : row?.id ? <button type="button" className="text-sm text-[var(--bo-link)]" onClick={() => setAskAdd(true)}>Add quotation</button> : <Link className="text-sm text-[var(--bo-link)]" to={`/quotations/new?customerId=${encodeURIComponent(customerId)}&title=${encodeURIComponent(title)}`}>New quotation</Link>}
            </SectionCard>
          </div>
        </div>
        {previewing ? (
          <div ref={previewRef} className="min-h-0 overflow-y-auto rounded-lg bg-[var(--bo-preview)] p-3">
            <ProposalPreview number={documentNumber || (row?.number ? String(row.number) : undefined)} title={title} issuedOn={issuedOn} customerName={customerName} preparedFor={preparedFor} preparedBy={preparedBy} summary={summary} purpose={purpose} understanding={understanding} approach={approach} scope={scope} stages={stages} deliverables={deliverables} commercial={commercial} assumptions={assumptions} whyMaxiron={whyMaxiron} nextSteps={nextSteps} />
          </div>
        ) : null}
        </div>
      )}
    </>
  );
  return (
    <>
      {bare ? (
        <div className="space-y-3">
          {saveAll ? null : <div className="flex justify-end gap-2">{row?.id ? <EditWithAiButton kind="proposal" status={row.status} onClick={() => setAssist('edit')} /> : <Button variant="outline" onClick={() => setAssist('create')}>Prepare with AI</Button>}<Button onClick={() => void save()}>Save</Button></div>}
          {formView}
        </div>
      ) : (
        <OfferWorkspace
          number={documentNumber || (row?.number ? String(row.number) : undefined)}
          title={title}
          customer={customerName}
          status={row?.poId ? 'committed' : row?.status ? String(row.status) : 'draft'}
          orderTo={row?.poId ? `/purchase-orders/${row.poId}` : undefined}
          orderLabel={row?.poNumber ? String(row.poNumber) : undefined}
          revision={row ? revisionLabel(row.revision).replace('Revision ', '') : '1'}
          saveState={saveState}
          tab={tab === 'activity' || tab === 'overview' ? tab : 'proposal'}
          proposalTo={onSelectTab ? undefined : (row?.id ? `/proposals/${row.id}` : undefined)}
          quotationTo={onSelectTab ? undefined : (linkedQuote ? `/quotations/${linkedQuote.id}` : undefined)}
          onSelectTab={onSelectTab}
          onMissing={onSelectTab || linkedQuote || !row?.id ? undefined : (part) => { if (part === 'quotation') setAskAdd(true); }}
          actions={
            <>
              {row?.id ? <EditWithAiButton kind="proposal" status={row.status} onClick={() => setAssist('edit')} /> : <Button variant="outline" onClick={() => setAssist('create')}>Prepare with AI</Button>}
              <Button variant="outline" onClick={() => setPreviewing((value) => !value)}>{previewing ? 'Edit' : 'Preview'}</Button>
              {row?.id && !linkedQuote && !onSelectTab ? <Button variant="outline" onClick={() => setAskAdd(true)}>Add quotation</Button> : null}
              {locked && !paired ? null : <Button onClick={() => void (saveAll ? saveAll() : save())}>Save</Button>}
              {row?.id && row.status === 'draft' ? <Button onClick={() => void (async () => { const ok = saveAll ? await saveAll() : await save(); if (ok) setAskReview(true); })()}>Review & issue</Button> : null}
              {row?.id ? <DropdownMenu trigger={<Button variant="outline" aria-label="More"><MoreHorizontal className="h-4 w-4" /></Button>} items={[
                { label: 'Duplicate', onClick: () => setAskDuplicate(true) },
                { label: 'PDF', onClick: () => window.open(`/api/proposals/${row.id}/pdf`, '_blank') },
                ...(row?.status === 'issued' ? [{ label: 'Revise', onClick: () => void revise() }] : []),
              ]} /> : null}
              <MarkStatus id={row?.id ? String(row.id) : undefined} url="/api/proposals" status={row?.status ? String(row.status) : undefined} onDone={setRow} />
            </>
          }
        >
          {formView}
        </OfferWorkspace>
      )}
      {assist ? <RecordAssistDialog kind={linkedQuote ? 'sales-offer' : 'proposal'} mode={assist} recordId={row?.id ? String(row.id) : undefined} proposalId={linkedQuote && row?.id ? String(row.id) : undefined} quotationId={linkedQuote?.id} proposalStatus={row?.status ? String(row.status) : undefined} quotationStatus={linkedQuote?.status} current={linkedQuote ? { proposal: body() } : body()} title={linkedQuote ? 'Offer' : 'Proposal'} onClose={() => setAssist(null)} onSaved={(saved) => { const next = String(saved.proposalId || ''); if (!row?.id && !recordId) { const open = next || String(saved.quotationId || saved.id || ''); if (open) navigate(next ? `/proposals/${open}` : `/quotations/${open}`); return; } if (next) void apiGet<Record<string, unknown>>(`/api/proposals/${next}`).then((found) => { setRow(found); applyProposal(found); }); }} /> : null}
      {askAdd && row?.id ? <AddOfferPartDialog kind="quotation" customerId={customerId} title={title} proposalId={String(row.id)} onClose={() => setAskAdd(false)} /> : null}
      {askDuplicate ? <OfferDuplicateDialog proposalId={row?.id ? String(row.id) : undefined} quotationId={linkedQuote?.id} onClose={() => setAskDuplicate(false)} /> : null}
      {askReview && row?.id ? <ReviewChoiceDialog reviewId={String(row.id)} proposalId={String(row.id)} quotationId={linkedQuote?.id} onClose={() => setAskReview(false)} /> : null}
    </>
  );
}
